import * as React from "react";
import type { OwuiChatSummary, OwuiFolder } from "@lupp/shared";
import { listChats } from "../services/chats";
import { getFolderChats, createFolder } from "../services/folders";
import { groupChatsByTime, formatChatAge } from "../services/chatGrouping";
import { buildFolderTree, folderPath, descendantIds, type FolderNode } from "../services/folderTree";
import { historyCache } from "../services/historyCache";

export interface ChatHistorySidebarProps {
  /** Whether the sidebar is docked beside the content (≥600px) vs. overlaying it. */
  docked: boolean;
  currentChatId?: string;
  onSelectChat: (chatId: string, folderId?: string | null) => void;
  onClose: () => void;
  folders: OwuiFolder[];
  activeFolderId: string | null;
  onEnterFolder: (folderId: string) => void;
  onProjectCreated: () => void;
  /** Bumped by the app whenever the chat list may have changed (new chat / saved turn). */
  refreshKey: number;
  /** Drag&drop: move a chat into a folder (folderId) or out to top level (null). */
  onMoveChat: (chatId: string, folderId: string | null) => void;
  /** Drag&drop: re-parent a folder (parentId) or move it to top level (null). */
  onMoveFolder: (folderId: string, parentId: string | null) => void;
}

/** Sentinel drop-target ids for the zones that aren't a specific folder. */
const CHATS_ZONE = "__chats__"; // chats → top level
const PROJECTS_ZONE = "__projects__"; // a folder → top level

interface ChatHistorySidebarState {
  chats: OwuiChatSummary[];
  loading: boolean;
  error: string | null;
  expandedFolders: Set<string>;
  folderChats: Record<string, OwuiChatSummary[]>; // lazily loaded
  folderChatsLoading: Set<string>;
  projekteOpen: boolean;
  chatsOpen: boolean;
  // create-project modal
  createOpen: boolean;
  createName: string;
  createDesc: string;
  creating: boolean;
  // drag & drop
  dragKind: "chat" | "folder" | null;
  dragId: string | null; // chat id or folder id being dragged
  dragSourceParent: string | null; // chat's folder / folder's parent (null = top level)
  dragForbidden: Set<string>; // folder ids that can't receive the drag (self + descendants)
  dropTarget: string | null; // folder id, CHATS_ZONE or PROJECTS_ZONE
}

// ── Collapse/expand prefs — global (not per document), localStorage. ───────────
const PREFS_KEY = "lupp.sidebarPrefs";
interface SidebarPrefs {
  projekteOpen: boolean;
  chatsOpen: boolean;
  expandedFolders: string[];
}
function loadPrefs(): SidebarPrefs {
  const defaults: SidebarPrefs = { projekteOpen: true, chatsOpen: true, expandedFolders: [] };
  try {
    return { ...defaults, ...(JSON.parse(localStorage.getItem(PREFS_KEY) ?? "{}") as Partial<SidebarPrefs>) };
  } catch {
    return defaults;
  }
}
function savePrefs(p: SidebarPrefs): void {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(p));
  } catch {
    /* best effort */
  }
}

/**
 * Right-docked chat history (OWUI-style). Projekte = nested folder tree (lazy chats,
 * "+" to create a project / add a chat) + Chats grouped by time. Collapse/expand state
 * is remembered globally; the active project's path always expands (deep link). A top
 * slot is reserved for future menu items.
 */
export default class ChatHistorySidebar extends React.Component<ChatHistorySidebarProps, ChatHistorySidebarState> {
  constructor(props: ChatHistorySidebarProps) {
    super(props);
    const prefs = loadPrefs();
    // Deep link: ensure the active project's full path is expanded even if saved collapsed.
    const expanded = new Set(prefs.expandedFolders);
    if (props.activeFolderId) folderPath(props.folders, props.activeFolderId).forEach((f) => expanded.add(f.id));
    // Seed from the warm cache so the sidebar shows content instantly (no loader/flicker).
    const cachedChats = historyCache.chats();
    const seedFolderChats: Record<string, OwuiChatSummary[]> = {};
    expanded.forEach((id) => {
      const fc = historyCache.folderChats(id);
      if (fc) seedFolderChats[id] = fc;
    });
    this.state = {
      chats: cachedChats ?? [],
      loading: cachedChats === null,
      error: null,
      expandedFolders: expanded,
      folderChats: seedFolderChats,
      folderChatsLoading: new Set(),
      projekteOpen: prefs.projekteOpen,
      chatsOpen: prefs.chatsOpen,
      createOpen: false,
      createName: "",
      createDesc: "",
      creating: false,
      dragKind: null,
      dragId: null,
      dragSourceParent: null,
      dragForbidden: new Set(),
      dropTarget: null,
    };
  }

  // ── Drag & drop ─────────────────────────────────────────────────────────────
  private onChatDragStart = (e: React.DragEvent, chatId: string, sourceFolderId: string | null): void => {
    e.dataTransfer.setData("text/plain", chatId);
    e.dataTransfer.effectAllowed = "move";
    this.setState({ dragKind: "chat", dragId: chatId, dragSourceParent: sourceFolderId, dragForbidden: new Set() });
  };
  private onFolderDragStart = (e: React.DragEvent, folderId: string, parentId: string | null): void => {
    e.dataTransfer.setData("text/plain", folderId);
    e.dataTransfer.effectAllowed = "move";
    // A folder can't be dropped onto itself or any of its own descendants (cycle).
    const forbidden = descendantIds(this.props.folders, folderId);
    forbidden.add(folderId);
    this.setState({ dragKind: "folder", dragId: folderId, dragSourceParent: parentId, dragForbidden: forbidden });
  };
  private onDragEnd = (): void => this.setState({ dragKind: null, dragId: null, dropTarget: null });

  /** Whether the current drag may drop on a given zone. */
  private zoneAccepts(zoneKind: "folder" | "chats" | "projects", target: string): boolean {
    const { dragKind, dragForbidden } = this.state;
    if (dragKind === "chat") return zoneKind === "folder" || zoneKind === "chats";
    if (dragKind === "folder") {
      if (zoneKind === "projects") return true;
      if (zoneKind === "folder") return !dragForbidden.has(target);
    }
    return false;
  }

  /** Hovering a drop zone. Innermost valid zone wins (stopPropagation); invalid → bubbles. */
  private onZoneDragOver = (e: React.DragEvent, target: string, zoneKind: "folder" | "chats" | "projects"): void => {
    if (!this.zoneAccepts(zoneKind, target)) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
    if (this.state.dropTarget !== target) this.setState({ dropTarget: target });
  };

  /** Drop on a zone. `newParent` is the resulting folder/parent (null = top level). */
  private onZoneDrop = (e: React.DragEvent, newParent: string | null, zoneKind: "folder" | "chats" | "projects"): void => {
    e.preventDefault();
    e.stopPropagation();
    const { dragKind, dragId, dragSourceParent } = this.state;
    this.setState({ dragKind: null, dragId: null, dropTarget: null });
    if (!dragId) return;
    // Parent unchanged → cancelled drop (touch nothing).
    if (dragSourceParent === newParent) return;
    if (dragKind === "chat") this.props.onMoveChat(dragId, newParent);
    else if (dragKind === "folder") this.props.onMoveFolder(dragId, newParent);
    void zoneKind;
  };

  /** Fires only when NOT over an inner zone (zones stopPropagation) → clear highlight. */
  private onBodyDragOver = (): void => {
    if (this.state.dragKind && this.state.dropTarget !== null) this.setState({ dropTarget: null });
  };

  componentDidMount(): void {
    this.reloadChats();
    // Load chats for every initially-expanded folder (saved + active path).
    this.state.expandedFolders.forEach((id) => this.loadFolderChats(id));
  }

  componentDidUpdate(prev: ChatHistorySidebarProps): void {
    if (prev.refreshKey !== this.props.refreshKey) {
      this.reloadChats();
      // Silently refresh the chats of currently-expanded folders (e.g. new chat / move).
      this.state.expandedFolders.forEach((id) => this.loadFolderChats(id));
    }
  }

  /** Re-fetch the chat list silently; update the cache. Keeps stale content on error
   *  (no flicker) and only shows an error if there's nothing cached to show. */
  private reloadChats(): void {
    listChats()
      .then((chats) => {
        historyCache.setChats(chats);
        this.setState({ chats, loading: false, error: null });
      })
      .catch((err: Error) =>
        this.setState((s) => ({ error: s.chats.length > 0 ? null : err.message || "Verlauf konnte nicht geladen werden.", loading: false })),
      );
  }

  private persist(): void {
    savePrefs({
      projekteOpen: this.state.projekteOpen,
      chatsOpen: this.state.chatsOpen,
      expandedFolders: Array.from(this.state.expandedFolders),
    });
  }

  /** Show a folder's chats: cached content instantly (no loader), then refresh silently.
   *  The loader only appears the FIRST time a folder is opened (nothing cached yet). */
  private loadFolderChats = (id: string): void => {
    const cached = historyCache.folderChats(id);
    if (cached && this.state.folderChats[id] === undefined) {
      this.setState((st) => ({ folderChats: { ...st.folderChats, [id]: cached } }));
    }
    if (this.state.folderChatsLoading.has(id)) return;
    const showLoader = this.state.folderChats[id] === undefined && cached === undefined;
    if (showLoader) {
      this.setState((s) => {
        const l = new Set(s.folderChatsLoading);
        l.add(id);
        return { folderChatsLoading: l };
      });
    }
    getFolderChats(id)
      .then((chats) => {
        historyCache.setFolderChats(id, chats);
        this.setState((st) => {
          const l = new Set(st.folderChatsLoading);
          l.delete(id);
          return { folderChats: { ...st.folderChats, [id]: chats }, folderChatsLoading: l };
        });
      })
      .catch(() =>
        this.setState((st) => {
          const l = new Set(st.folderChatsLoading);
          l.delete(id);
          return { folderChatsLoading: l }; // keep any cached content on error
        }),
      );
  };

  private toggleFolder = (id: string): void => {
    const willExpand = !this.state.expandedFolders.has(id);
    this.setState(
      (s) => {
        const expanded = new Set(s.expandedFolders);
        if (willExpand) expanded.add(id);
        else expanded.delete(id);
        return { expandedFolders: expanded };
      },
      () => this.persist(),
    );
    if (willExpand) this.loadFolderChats(id);
  };

  private toggleSection = (key: "projekteOpen" | "chatsOpen"): void => {
    this.setState((s) => ({ [key]: !s[key] }) as Pick<ChatHistorySidebarState, typeof key>, () => this.persist());
  };

  private submitCreate = (): void => {
    const name = this.state.createName.trim();
    if (!name || this.state.creating) return;
    this.setState({ creating: true });
    createFolder(name, this.state.createDesc.trim() || undefined)
      .then(() => {
        this.setState({ creating: false, createOpen: false, createName: "", createDesc: "" });
        this.props.onProjectCreated();
      })
      .catch(() => this.setState({ creating: false }));
  };

  private renderChatItem = (c: OwuiChatSummary, folderId?: string): JSX.Element => {
    const age = formatChatAge(c, Date.now());
    return (
      <button
        key={c.id}
        type="button"
        draggable
        className={`chat-history__item${c.id === this.props.currentChatId ? " chat-history__item--active" : ""}${this.state.dragKind === "chat" && this.state.dragId === c.id ? " chat-history__item--dragging" : ""}`}
        title={c.title}
        onDragStart={(e): void => this.onChatDragStart(e, c.id, folderId ?? null)}
        onDragEnd={this.onDragEnd}
        onClick={(): void => this.props.onSelectChat(c.id, folderId ?? null)}
      >
        <span className="chat-history__item-title">{c.title || "Unbenannter Chat"}</span>
        {age && <span className="chat-history__item-age">{age}</span>}
      </button>
    );
  };

  private renderFolderNode = (node: FolderNode, depth: number): JSX.Element => {
    const { expandedFolders, folderChats, folderChatsLoading } = this.state;
    const { id, name, description } = node.folder;
    const open = expandedFolders.has(id);
    const chats = folderChats[id] ?? [];
    const loadingChats = folderChatsLoading.has(id);
    const isDrop = this.state.dropTarget === id;
    return (
      <div
        key={id}
        className={`chat-history__folder${isDrop ? " chat-history__folder--drop" : ""}`}
        onDragOver={(e): void => this.onZoneDragOver(e, id, "folder")}
        onDrop={(e): void => this.onZoneDrop(e, id, "folder")}
      >
        {/* Indentation is handled by .chat-history__folder-children (padding + guide line),
            not per-row, so nesting deepens uniformly and a thin vertical line can be drawn. */}
        <div className="chat-history__folder-row" title={description || name}>
          <button type="button" className="chat-history__folder-twirl" aria-label={open ? "Einklappen" : "Ausklappen"} aria-expanded={open} onClick={(): void => this.toggleFolder(id)}>
            <Chevron open={open} />
          </button>
          <button
            type="button"
            className="chat-history__folder-open"
            draggable
            onDragStart={(e): void => this.onFolderDragStart(e, id, node.folder.parentId)}
            onDragEnd={this.onDragEnd}
            onClick={(): void => this.props.onEnterFolder(id)}
          >
            {depth > 0 && <FolderIcon />}
            <span className="chat-history__folder-name">{name}</span>
          </button>
          <button type="button" className="chat-history__folder-add" aria-label="Chat in Projekt hinzufügen" title="Chat hinzufügen" onClick={(): void => this.props.onEnterFolder(id)}>
            <PlusIcon />
          </button>
        </div>
        {open && (
          <div className="chat-history__folder-children">
            {node.children.map((child) => this.renderFolderNode(child, depth + 1))}
            {loadingChats && <div className="chat-history__empty">Lädt …</div>}
            {!loadingChats && chats.length === 0 && node.children.length === 0 && <div className="chat-history__empty">Leer.</div>}
            {chats.map((c) => this.renderChatItem(c, id))}
          </div>
        )}
      </div>
    );
  };

  render(): JSX.Element {
    const { docked, onClose, folders } = this.props;
    const { chats, loading, error, projekteOpen, chatsOpen, createOpen, createName, createDesc, creating } = this.state;
    const groups = groupChatsByTime(chats, Date.now());
    const tree = buildFolderTree(folders);

    return (
      <aside className={`chat-history${docked ? " chat-history--docked" : " chat-history--overlay"}`} aria-label="Chat-Verlauf">
        <div className="chat-history__header">
          <span className="chat-history__title">Verlauf</span>
          <button className="icon-btn" type="button" aria-label="Verlauf schließen" title="Schließen" onClick={onClose}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Reserved slot for future menu items (chat search etc.) */}
        <div className="chat-history__menu" aria-hidden="true" />

        <div className="chat-history__body" onDragOver={this.onBodyDragOver}>
          {/* Projekte (nested folders). Also a drop zone for folders → move to top level. */}
          <section
            className={`chat-history__section chat-history__projects-zone${this.state.dropTarget === PROJECTS_ZONE ? " chat-history__projects-zone--drop" : ""}`}
            onDragOver={(e): void => this.onZoneDragOver(e, PROJECTS_ZONE, "projects")}
            onDrop={(e): void => this.onZoneDrop(e, null, "projects")}
          >
            <div className="chat-history__section-head">
              <button className="chat-history__section-toggle" type="button" aria-expanded={projekteOpen} onClick={(): void => this.toggleSection("projekteOpen")}>
                <Chevron open={projekteOpen} />
                <span>Projekte</span>
              </button>
              <button className="chat-history__section-add" type="button" aria-label="Projekt anlegen" title="Projekt anlegen" onClick={(): void => this.setState({ createOpen: true })}>
                <PlusIcon />
              </button>
            </div>
            {projekteOpen && (
              <div className="chat-history__folders">
                {tree.length === 0 && <div className="chat-history__empty">Noch keine Projekte.</div>}
                {tree.map((n) => this.renderFolderNode(n, 0))}
              </div>
            )}
          </section>

          {/* Chats — main section, grouped by time. Also a drop zone → move to top level. */}
          <section
            className={`chat-history__section chat-history__chats-zone${this.state.dropTarget === CHATS_ZONE ? " chat-history__chats-zone--drop" : ""}`}
            onDragOver={(e): void => this.onZoneDragOver(e, CHATS_ZONE, "chats")}
            onDrop={(e): void => this.onZoneDrop(e, null, "chats")}
          >
            <div className="chat-history__section-head">
              <button className="chat-history__section-toggle" type="button" aria-expanded={chatsOpen} onClick={(): void => this.toggleSection("chatsOpen")}>
                <Chevron open={chatsOpen} />
                <span>Chats</span>
              </button>
            </div>

            {chatsOpen && (
              <div className="chat-history__chats">
                {loading && <div className="chat-history__empty">Lädt …</div>}
                {error && <div className="chat-history__empty chat-history__empty--error">{error}</div>}
                {!loading && !error && chats.length === 0 && <div className="chat-history__empty">Keine Chats.</div>}
                {!loading &&
                  !error &&
                  groups.map((g) => (
                    <div key={g.label} className="chat-history__group">
                      <div className="chat-history__group-label">{g.label}</div>
                      {g.chats.map((c) => this.renderChatItem(c))}
                    </div>
                  ))}
              </div>
            )}
          </section>
        </div>

        {createOpen && (
          <div className="chat-history__create-overlay" role="dialog" aria-label="Projekt anlegen">
            <div className="chat-history__create">
              <div className="chat-history__create-title">Neues Projekt</div>
              <input
                className="chat-history__create-input"
                type="text"
                placeholder="Projektname"
                value={createName}
                onChange={(e): void => this.setState({ createName: e.target.value })}
                onKeyDown={(e): void => {
                  if (e.key === "Enter") this.submitCreate();
                }}
                autoFocus
              />
              <textarea
                className="chat-history__create-input chat-history__create-desc"
                placeholder="Beschreibung (optional)"
                value={createDesc}
                rows={3}
                onChange={(e): void => this.setState({ createDesc: e.target.value })}
              />
              <div className="chat-history__create-actions">
                <button type="button" className="chat-history__btn" onClick={(): void => this.setState({ createOpen: false })}>
                  Abbrechen
                </button>
                <button type="button" className="chat-history__btn chat-history__btn--primary" disabled={!createName.trim() || creating} onClick={this.submitCreate}>
                  {creating ? "Lege an …" : "Anlegen"}
                </button>
              </div>
            </div>
          </div>
        )}
      </aside>
    );
  }
}

function Chevron({ open }: { open: boolean }): JSX.Element {
  return (
    <svg
      className={`chat-history__chevron${open ? " chat-history__chevron--open" : ""}`}
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

function FolderIcon(): JSX.Element {
  return (
    <svg className="chat-history__folder-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 5a2 2 0 0 1 2-2h3.6l2 2H18a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z" />
    </svg>
  );
}

function PlusIcon(): JSX.Element {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}
